import type { Sort } from "../data/query";
import type { Filter, FilterCondition } from "../filters/model";
import type { SavedView } from "../types/access";
import { RETURNING_STAGES } from "./tracking";

/**
 * The shared saved views that come with shipment tracking (#527): the ones the
 * dashboard's cards (#528) count and open.
 *
 * Defined here, once, and read by everyone who needs the meaning of one: the
 * API seeds them as `saved_views` rows at start, the fixture adapter lists them
 * beside its own, the list screens offer them like any saved view, and #528 can
 * import a definition and count its rows or build the link to it, so that a
 * card and the list it opens cannot disagree.
 *
 * Each exists on **Shipments** (`orders.shipments`, the fields `stage` and
 * `health`) and on **Orders** (`orders`, where the one shipment that speaks for
 * an order is `shipmentStage` and `shipmentHealth`). The order-level fields
 * answer every one of these questions, so every view is offered on both.
 *
 * They are rows, so an administrator can still change a view's filter,
 * columns and sort without a deploy, and make one the default. What they cannot
 * do is vanish or be renamed by accident: the API refuses to delete or rename
 * one (`isSharedViewIdentity`), and the picker does not offer either. The
 * identity of a view is its scope and name, the pair the table already keeps
 * unique, so a card can look one up by it.
 */

/** Which list a shared view is on. */
export type SharedViewList = "shipments" | "orders" | "inventory" | "contacts";

/** The lists the tracking views are on; Stock Levels has one view of its own. */
type TrackingList = Exclude<SharedViewList, "inventory" | "contacts">;

/** The saved-view scope of each list: the section's own, as `SectionPage` names it. */
export const SHARED_VIEW_SCOPES: Record<SharedViewList, string> = {
  shipments: "orders.shipments",
  orders: "orders",
  // Stock Levels is the first section of Inventory, so it keeps the bare module id.
  inventory: "inventory",
  // All Contacts is the first section of Contacts, likewise.
  contacts: "contacts",
};

/** What a shared view is, before it is a row. */
export interface SharedViewDefinition {
  /** Stable, for code to name one by: `no_update`. Never stored. */
  key: SharedViewKey;
  list: SharedViewList;
  /** `SHARED_VIEW_SCOPES[list]`. */
  scope: string;
  name: string;
  filter: Filter;
  columns: string[];
  sort: Sort[];
}

export const SHARED_VIEW_KEYS = [
  "no_update",
  "delayed",
  "attempt_failed",
  "returning",
  "rto_delivered",
  // Order stage (#465), on Orders only: a shipment has no confirmation call.
  "cod_awaiting_confirmation",
  "overdue_confirmation",
  "not_shipped_24h",
  // Stock Levels (#602 part B), where Low Stock used to be a page of its own.
  "low_stock",
  // All Contacts (#637).
  "repeat_buyers",
  "new_this_month",
] as const;

export type SharedViewKey = (typeof SHARED_VIEW_KEYS)[number];

/** The names a person sees, which are also the identity of the row. */
export const SHARED_VIEW_NAMES: Record<SharedViewKey, string> = {
  no_update: "No update",
  delayed: "Delayed",
  attempt_failed: "Attempt failed",
  returning: "Returning",
  // The courier says the parcel has come back and nobody has received it (#529).
  rto_delivered: "RTO delivered, not yet received",
  cod_awaiting_confirmation: "COD awaiting confirmation",
  overdue_confirmation: "Overdue confirmation",
  not_shipped_24h: "Not shipped over 24 hours",
  low_stock: "Low stock",
  repeat_buyers: "Repeat buyers",
  new_this_month: "New this month",
};

/** The field names each list calls its tracking fields. */
const FIELDS: Record<TrackingList, { stage: string; health: string }> = {
  shipments: { stage: "stage", health: "health" },
  orders: { stage: "shipmentStage", health: "shipmentHealth" },
};

/**
 * Columns each view opens with: the tracking ones first, since the question a
 * view asks is a tracking question, and what is needed to act on a row.
 */
const COLUMNS: Record<TrackingList, string[]> = {
  shipments: [
    "orderNumber",
    "courier",
    "trackingNumber",
    "stage",
    "health",
    "attemptCount",
    "lastScanAt",
    "expectedDeliveryDate",
    "destinationState",
  ],
  orders: [
    "orderNumber",
    "placedAt",
    "customerName",
    "shipmentStage",
    "shipmentHealth",
    "attemptCount",
    "lastScanAt",
    "expectedDeliveryDate",
    "total",
  ],
};

/** A condition, built with its keys in the order the URL decoder builds them. */
function condition(
  field: string,
  operator: FilterCondition["operator"],
  value: FilterCondition["value"],
): FilterCondition {
  return { kind: "condition", field, operator, value };
}

/**
 * A condition with no value to compare against (`is_true`, `is_false`): built
 * without a `value` key, as the URL decoder builds one, so a view read back
 * from jsonb matches the same filter from the URL.
 */
function flag(
  field: string,
  operator: "is_true" | "is_false",
): FilterCondition {
  return { kind: "condition", field, operator };
}

function group(...children: FilterCondition[]): Filter {
  return { kind: "group", combinator: "and", children };
}

function build(
  key: SharedViewKey,
  list: TrackingList,
  filter: (fields: { stage: string; health: string }) => Filter,
  sort: Sort[],
): SharedViewDefinition {
  return {
    key,
    list,
    scope: SHARED_VIEW_SCOPES[list],
    name: SHARED_VIEW_NAMES[key],
    filter: filter(FIELDS[list]),
    columns: [...COLUMNS[list]],
    sort,
  };
}

/** Longest waiting first: the one that has been quiet the longest is the one to chase. */
const OLDEST_UPDATE_FIRST: Sort[] = [
  { column: "lastScanAt", direction: "asc" },
];

function definitionsFor(list: TrackingList): SharedViewDefinition[] {
  return [
    build(
      "no_update",
      list,
      (f) => group(condition(f.health, "is", "no_update")),
      OLDEST_UPDATE_FIRST,
    ),
    build("delayed", list, (f) => group(condition(f.health, "is", "delayed")), [
      { column: "expectedDeliveryDate", direction: "asc" },
    ]),
    build(
      "attempt_failed",
      list,
      (f) => group(condition(f.stage, "is", "attempt_failed")),
      OLDEST_UPDATE_FIRST,
    ),
    build(
      "returning",
      list,
      (f) => group(condition(f.stage, "is_any_of", [...RETURNING_STAGES])),
      OLDEST_UPDATE_FIRST,
    ),
    // Narrowed by #529: the courier says the parcel is back and nobody has
    // received it yet. `rtoReceived` is the field both lists call it.
    build(
      "rto_delivered",
      list,
      (f) =>
        group(
          condition(f.stage, "is", "rto_delivered"),
          flag("rtoReceived", "is_false"),
        ),
      OLDEST_UPDATE_FIRST,
    ),
  ];
}

/**
 * The two views that come with order stage (#465): COD orders still New, and
 * those of them past the overdue limit. Orders only, oldest first, since the
 * one placed longest ago is the one to call.
 */
function stageViews(): SharedViewDefinition[] {
  const columns = [
    "orderNumber",
    "stage",
    "confirmation",
    "placedAt",
    "customerName",
    "total",
  ];
  const oldestFirst: Sort[] = [{ column: "placedAt", direction: "asc" }];
  const make = (key: SharedViewKey, filter: Filter): SharedViewDefinition => ({
    key,
    list: "orders",
    scope: SHARED_VIEW_SCOPES.orders,
    name: SHARED_VIEW_NAMES[key],
    filter,
    columns: [...columns],
    sort: oldestFirst.map((entry) => ({ ...entry })),
  });
  return [
    make("cod_awaiting_confirmation", group(condition("stage", "is", "new"))),
    make("overdue_confirmation", group(flag("overdue", "is_true"))),
    // Ready to ship (Confirmed means prepaid or a confirmed COD, with no shipment yet).
    make("not_shipped_24h", group(flag("notShipped", "is_true"))),
  ];
}

/**
 * The view that replaces the Low Stock page (#602 part B): levels at or below
 * their threshold, the lowest first. `belowThreshold` is the field the server
 * computes from the level's own threshold or the store default, so this and the
 * chip on Stock Levels can never disagree about what "low" means.
 */
function lowStockViews(): SharedViewDefinition[] {
  return [
    {
      key: "low_stock",
      list: "inventory",
      scope: SHARED_VIEW_SCOPES.inventory,
      name: SHARED_VIEW_NAMES.low_stock,
      filter: group(flag("belowThreshold", "is_true")),
      columns: [
        "productTitle",
        "sku",
        "warehouse",
        "available",
        "lowStockThreshold",
      ],
      sort: [{ column: "available", direction: "asc" }],
    },
  ];
}

/**
 * The two views that come with All Contacts (#637). Repeat buyers: customers
 * with two or more orders that were not cancelled, the biggest spenders first.
 * New this month: contacts and customers added in the last 30 days, newest
 * first — a rolling month, since the filter model has no calendar month.
 */
function contactViews(): SharedViewDefinition[] {
  const make = (
    key: SharedViewKey,
    filter: Filter,
    columns: string[],
    sort: Sort[],
  ): SharedViewDefinition => ({
    key,
    list: "contacts",
    scope: SHARED_VIEW_SCOPES.contacts,
    name: SHARED_VIEW_NAMES[key],
    filter,
    columns,
    sort,
  });
  return [
    make(
      "repeat_buyers",
      group(
        condition("kind", "is", "customer"),
        condition("ordersCount", "gte", 2),
      ),
      [
        "displayName",
        "phone",
        "city",
        "ordersCount",
        "totalSpent",
        "lastOrderAt",
        "tags",
      ],
      [{ column: "totalSpent", direction: "desc" }],
    ),
    make(
      "new_this_month",
      group(condition("createdAt", "in_last_days", 30)),
      [
        "displayName",
        "kind",
        "phone",
        "city",
        "ordersCount",
        "totalSpent",
        "createdAt",
      ],
      [{ column: "createdAt", direction: "desc" }],
    ),
  ];
}

/** Every shared view, Shipments' first. */
export const SHARED_TRACKING_VIEWS: readonly SharedViewDefinition[] = [
  ...definitionsFor("shipments"),
  ...definitionsFor("orders"),
  ...stageViews(),
  ...lowStockViews(),
  ...contactViews(),
];

/** One shared view by its key and list: what #528's card imports. */
export function sharedViewDefinition(
  key: SharedViewKey,
  list: SharedViewList,
): SharedViewDefinition {
  const found = SHARED_TRACKING_VIEWS.find(
    (view) => view.key === key && view.list === list,
  );
  if (!found) throw new Error(`no shared view ${key} on ${list}`);
  return found;
}

/**
 * Whether a view is one of these, by what identifies it: its list and name,
 * compared the way the table's unique index compares them (case-insensitive).
 * The server will not delete or rename such a view, and the picker does not
 * offer to.
 */
export function isSharedViewIdentity(scope: string, name: string): boolean {
  const lower = name.trim().toLowerCase();
  return SHARED_TRACKING_VIEWS.some(
    (view) => view.scope === scope && view.name.toLowerCase() === lower,
  );
}

/** What a refusal to delete or rename one says. */
export const SHARED_VIEW_PROTECTED_MESSAGE =
  "This view comes with the application, and the dashboard counts it. Its filter and columns can be changed; it cannot be renamed or deleted.";

/**
 * A definition as the `SavedView` the list screens read, for an adapter that
 * keeps views in memory. The id is the adapter's own.
 */
export function asSavedView(
  definition: SharedViewDefinition,
  id: string,
  at: string,
): SavedView {
  return {
    id,
    scope: definition.scope,
    name: definition.name,
    ownerUserId: null,
    isShared: true,
    isDefault: false,
    filter: definition.filter,
    columns: [...definition.columns],
    sort: definition.sort.map((entry) => ({ ...entry })),
    createdAt: at,
    updatedAt: at,
  };
}
